import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { addDays, format } from 'date-fns';
import {
  FieldData,
  NameOfFrequency,
  TextField,
  Select,
  DatepickerSimple,
  Timestamp,
  Tooltip,
  IsTodayDate,
  Flag,
  withStyles,
  MaterialSymbol,
  AccountFundingOption,
  FundingSettlementTime,
  getMaskedNumber,
  Box,
} from 'vopay';
import { Skeleton } from 'UI';
import { useClientData } from '@/providers/ClientData.provider';
import { useAccountInfo } from '@/providers/AccountInfo.provider';
import { useI18nLabels } from '@/utils/useI18nLabels';
import { INTERAC_MESSAGE_MAX_LENGTH } from '@/constants';
import { MyAccountFormData } from '../types';
import styles from '@/styles/PaymentAndCollection/PaymentAndCollection.module.scss';

const cn = withStyles(styles);

const cadOnlyOptions = [{ value: 'CAD', label: 'CAD' }];
const usdOnlyOptions = [{ value: 'USD', label: 'USD' }];

const cadUsdOptions = [
  { value: 'CAD', label: 'CAD' },
  { value: 'USD', label: 'USD' },
];

type FundingSourceOption = {
  value: 'bank' | 'credit';
  icon: 'account_balance' | 'credit_card';
  title: string;
  subtitle: string;
  settlementTime: string;
  fee: string;
  isFree: boolean;
};

type UseFundAccountFieldsProps = {
  fundingOptions?: AccountFundingOption[];
  fetchingFundingOptions?: boolean;
};

export const useFundAccountFields = ({
  fundingOptions = [],
  fetchingFundingOptions = false,
}: UseFundAccountFieldsProps = {}) => {
  const {
    response: { USDEFTSendEnabled, clientAccountsEnabled, TransactionLabelOverrideEnabled, Country },
  } = useClientData();

  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.FundMyAccount.Form' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const { frequencyLabelMapper } = useI18nLabels();
  const { setNewCurrencyInfo } = useAccountInfo();
  const now = format(new Date(), 'yyyy-MM-dd');

  const frequencyOptions = (
    [
      'daily',
      'weekly',
      'bi-weekly',
      'monthly',
      'semi-monthly',
      'quarterly',
      'semi-annually',
      'annually',
      'weekdays',
    ] as NameOfFrequency[]
  ).map((v) => ({ value: v, label: frequencyLabelMapper(v) }));

  const scheduleTypeOptions = [
    { value: 'single', label: tGlobal('ScheduleType.single') },
    { value: 'recurring', label: tGlobal('ScheduleType.recurring') },
  ];

  const endingOptions = [
    { value: 'End Date', label: tGlobal('EndingOptions.endDate') },
    { value: 'Payments', label: tGlobal('EndingOptions.payments') },
  ];

  const semiMonthlyDateOptions = [
    { value: 'SemiMonthFirstAndFifteenth', label: tGlobal('SemiMonthlyDateOptions.firstAndFifteenth') },
    { value: 'SemiMonthFifteenthAndLast', label: tGlobal('SemiMonthlyDateOptions.fifteenthAndLast') },
  ];

  const isScheduleEligible = (ctx: Partial<MyAccountFormData>) =>
    ctx.PaymentMethod === 'Credit Card' ||
    (ctx.PaymentMethod === 'Bank Account' &&
      ((Country === 'CA' && ctx.Currency === 'CAD') || (Country === 'US' && ctx.Currency === 'USD')));

  const bankFundingOption = fundingOptions.find((o) => o.Source === 'bank');
  const creditFundingOption = fundingOptions.find((o) => o.Source === 'credit');

  const settlementTimeLabels: Record<FundingSettlementTime, string> = {
    immediately: t('FundingSource.SettlementTime.immediately', { defaultValue: 'Immediately' }),
    t1: t('FundingSource.SettlementTime.t1', { defaultValue: '1 business day' }),
    t2: t('FundingSource.SettlementTime.t2', { defaultValue: '2 business days' }),
    t3: t('FundingSource.SettlementTime.t3', { defaultValue: '3 business days' }),
  };

  const formatSettlementTime = (code: FundingSettlementTime | undefined) => (code && settlementTimeLabels[code]) || '';

  const isBankFundingFree = !bankFundingOption?.FeePerTransaction || bankFundingOption.FeePerTransaction === '0.00';

  const bankSourceOption: FundingSourceOption | null = bankFundingOption
    ? {
        value: 'bank',
        icon: 'account_balance',
        title: `${t('FundingSource.bankAccount', { defaultValue: 'Bank Account' })} ${bankFundingOption.AccountNumber || ''}`,
        subtitle: `${bankFundingOption.Currency} ${bankFundingOption.AccountHolderName || ''}`,
        settlementTime: formatSettlementTime(bankFundingOption.SettlementTime),
        fee: isBankFundingFree
          ? t('FundingSource.noFee', { defaultValue: 'No fee' })
          : `$ ${bankFundingOption.FeePerTransaction || ''}`,
        isFree: isBankFundingFree,
      }
    : null;

  const isCreditFundingFree = !creditFundingOption?.FeePercent || Number(creditFundingOption.FeePercent) === 0;

  const creditSourceOption: FundingSourceOption | null = creditFundingOption
    ? {
        value: 'credit',
        icon: 'credit_card',
        title: `${t('FundingSource.creditCard', { defaultValue: 'Credit Card' })} ${getMaskedNumber(
          creditFundingOption.CreditCardNumber || '',
        )}`,
        subtitle: `${creditFundingOption.Currency} ${creditFundingOption.CreditCardHolderName || ''}`,
        settlementTime: formatSettlementTime(creditFundingOption.SettlementTime),
        fee: isCreditFundingFree
          ? t('FundingSource.noFee', { defaultValue: 'No fee' })
          : `${creditFundingOption.FeePercent}%`,
        isFree: isCreditFundingFree,
      }
    : null;

  const fundingSourceOptions = [bankSourceOption, creditSourceOption].filter(
    (option): option is FundingSourceOption => option !== null,
  );

  const accountFields: FieldData<MyAccountFormData>[] = [
    {
      label: () => t('FundingSource.label', { defaultValue: 'Funding Source' }),
      fieldName: 'Account',
      getInputState: () => 'required',
      showField: () => !clientAccountsEnabled,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});

        if (fetchingFundingOptions) return <Skeleton height="32px" />;

        return (
          <Select
            version="2"
            placeholder={t('Account.placeholder', { defaultValue: 'Please Select...' })}
            options={fundingSourceOptions}
            optionValue={(o) => o.value}
            {...fieldData}
            renderSelectedValue={(o, { Placeholder }) =>
              o && bankFundingOption ? <div className={cn('paymentName')}>{o.title}</div> : <Placeholder />
            }
            renderOption={(o, { Option, selected }) => (
              <Option>
                <Box gapSize="12px" ai="center">
                  <MaterialSymbol
                    icon={selected ? 'check' : o.icon}
                    size="20px"
                    color="#444"
                    fillAmount={o.value === 'bank' ? 1 : 0}
                  />
                  <Box gapSize="2px" flx="col">
                    <div className={cn('paymentName', 'mediumWeight')}>{o.title}</div>
                    <div className={cn('paymentInfo')}>{o.subtitle}</div>
                  </Box>
                </Box>
                <Box flx="col" ai="fe" gapSize="4px">
                  <div className={cn('smallFont')}>{o.settlementTime}</div>
                  <div className={cn(o.isFree && 'green', 'mediumWeight', 'smallFont')}>{o.fee}</div>
                </Box>
              </Option>
            )}
          />
        );
      },
      validate: () => (!clientAccountsEnabled ? Yup.string().required() : Yup.string()),
    },
    {
      label: () => t('ClientAccount.label', { defaultValue: 'Client Account' }),
      fieldName: 'ClientAccount',
      getInputState: () => 'required',
      showField: () => clientAccountsEnabled,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});

        return (
          <TextField
            variant="outlineShadow"
            preset="text"
            readOnly
            placeholder={t('Account.placeholder', { defaultValue: 'Please Select...' })}
            {...fieldData}
          />
        );
      },
      validate: () => (clientAccountsEnabled ? Yup.string().required() : Yup.string()),
    },
    {
      label: () => t('paymentMethod', { defaultValue: 'Payment Method' }),
      fieldName: 'PaymentMethod',
      getInputState: () => 'required',
      showField: () => clientAccountsEnabled,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});

        return <TextField variant="outlineShadow" preset="text" readOnly {...fieldData} />;
      },
      validate: () => Yup.string().required(),
    },
  ];

  const amountFields: FieldData<MyAccountFormData>[] = [
    {
      label: () => t('amount', { defaultValue: 'Amount' }),
      fieldName: 'Amount',
      getInputState: () => 'required',
      showField: () => true,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});

        return <TextField variant="outlineShadow" preset="currency" placeholder="0.00" {...fieldData} />;
      },
      validate: () =>
        Yup.string()
          .test(
            'amount-validation',
            t('ErrorMessage.amountMinValue', {
              defaultValue: 'This value should be greater than or equal to $0.01.',
            }),
            (value) => Number(value?.replaceAll(',', '')) > 0,
          )
          .required(),
    },
    {
      label: () => <span>&nbsp;</span>,
      fieldName: 'Currency',
      getInputState: () => 'optional',
      showField: () => true,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});

        const currencyOpts =
          Country === 'US' ? usdOnlyOptions : USDEFTSendEnabled === '0' ? cadOnlyOptions : cadUsdOptions;

        return (
          <Select
            version="2"
            options={currencyOpts}
            {...fieldData}
            onChange={(e) => {
              setNewCurrencyInfo(e.target.value);
              fieldData.onChange?.(e);
            }}
            renderSelectedValue={(o, { disabled }) => (
              <div className={cn('currencyOption')}>
                <Flag className={cn(disabled && 'iconDisabled')} flag={o?.value || ''} width="24px" />
                {o?.label}
              </div>
            )}
            renderOption={(o, { Option }) => (
              <Option style={{ justifyContent: 'flex-start' }}>
                <div className={cn('currencyOption')}>
                  <Flag flag={o.value} width="24px" /> {o.label}
                </div>
              </Option>
            )}
          />
        );
      },
      validate: () => Yup.string().required(),
    },
  ];

  const scheduleFields: FieldData<MyAccountFormData>[] = [
    {
      label: () => t('scheduleType', { defaultValue: 'Schedule Type' }),
      fieldName: 'ScheduleType',
      getInputState: () => 'required',
      showField: (ctx) => isScheduleEligible(ctx),
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});

        return <Select version="2" options={scheduleTypeOptions} {...fieldData} />;
      },
      validate: () =>
        Yup.string().when(['PaymentMethod', 'Currency'], {
          is: (PaymentMethod: string, Currency: string) => isScheduleEligible({ PaymentMethod, Currency }),
          then: Yup.string().required(),
          otherwise: Yup.string(),
        }),
    },
    {
      label: () => t('TakeFromAccountOn.label', { defaultValue: 'Funding Date' }),
      fieldName: 'TakeFromAccountOn',
      getInputState: () => 'required',
      showField: (ctx) => isScheduleEligible(ctx) && ctx.ScheduleType !== 'recurring',
      tooltip: () => (
        <Tooltip
          type="hover"
          information={t('TakeFromAccountOn.toolTip', {
            defaultValue:
              'The payment/collection transaction will be initiated immediately after the user has connected their bank account.',
          })}
          position="right"
          hasArrow={false}
          icon="question"
        />
      ),
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});

        return (
          <DatepickerSimple
            {...fieldData}
            variant="outlineShadow"
            placeholder="YYYY-MM-DD"
            iconPosition="left"
            min={new Date().getTime() as Timestamp}
            isDisabled={fieldData.disabled}
          />
        );
      },
      validate: () =>
        Yup.string().when(['PaymentMethod', 'ScheduleType', 'Currency'], {
          is: (PaymentMethod: string, ScheduleType: string, Currency: string) =>
            isScheduleEligible({ PaymentMethod, Currency }) && ScheduleType === 'single',
          then: Yup.string()
            .test(
              'take-from-account-validation',
              t('ErrorMessage.takeFromAccountOnInvalidStartDate', {
                startDate: now,
                defaultValue: 'Invalid date please choose a date on or after {{startDate}}',
              }),
              (value) => !(value !== 'undefined' && format(new Date(Number(value)), 'yyyy-MM-dd') < now),
            )
            .required(),
          otherwise: Yup.string(),
        }),
    },
    {
      label: () => t('startDate', { defaultValue: 'Start Date' }),
      fieldName: 'StartDate',
      getInputState: () => 'required',
      showField: (ctx) => isScheduleEligible(ctx) && ctx.ScheduleType === 'recurring',
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});

        return (
          <DatepickerSimple
            {...fieldData}
            variant="outlineShadow"
            placeholder="YYYY-MM-DD"
            iconPosition="left"
            min={addDays(new Date(), 1).getTime() as Timestamp}
            isDisabled={fieldData.disabled}
          />
        );
      },
      validate: () =>
        Yup.string().when(['PaymentMethod', 'ScheduleType', 'Currency'], {
          is: (PaymentMethod: string, ScheduleType: string, Currency: string) =>
            isScheduleEligible({ PaymentMethod, Currency }) && ScheduleType === 'recurring',
          then: Yup.string()
            .test(
              'start-date-validation',
              t('ErrorMessage.invalidStartDate', {
                startDate: format(addDays(new Date(), 1), 'yyyy-MM-dd'),
                defaultValue: 'Invalid date. Please choose a date on or after {{startDate}}',
              }),
              (value) =>
                !(
                  value !== 'undefined' &&
                  format(new Date(Number(value)), 'yyyy-MM-dd') < format(addDays(new Date(), 1), 'yyyy-MM-dd')
                ),
            )
            .required(),
          otherwise: Yup.string(),
        }),
    },
    {
      label: () => t('frequency', { defaultValue: 'Frequency' }),
      fieldName: 'Frequency',
      getInputState: () => 'required',
      showField: (ctx) => isScheduleEligible(ctx) && ctx.ScheduleType === 'recurring',
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});

        return <Select version="2" options={frequencyOptions} {...fieldData} />;
      },
      validate: () =>
        Yup.string().when(['PaymentMethod', 'ScheduleType', 'Currency'], {
          is: (PaymentMethod: string, ScheduleType: string, Currency: string) =>
            isScheduleEligible({ PaymentMethod, Currency }) && ScheduleType === 'recurring',
          then: Yup.string().required(),
          otherwise: Yup.string(),
        }),
    },
    {
      label: () => t('semiMonthlyDates', { defaultValue: 'Semi-Monthly Dates' }),
      fieldName: 'SemiMonthlyDates',
      getInputState: () => 'required',
      showField: (ctx) =>
        isScheduleEligible(ctx) && ctx.ScheduleType === 'recurring' && ctx.Frequency === 'semi-monthly',
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});

        return <Select version="2" options={semiMonthlyDateOptions} {...fieldData} />;
      },
      validate: () =>
        Yup.string().when(['PaymentMethod', 'ScheduleType', 'Frequency', 'Currency'], {
          is: (PaymentMethod: string, ScheduleType: string, Frequency: string, Currency: string) =>
            isScheduleEligible({ PaymentMethod, Currency }) &&
            ScheduleType === 'recurring' &&
            Frequency === 'semi-monthly',
          then: Yup.string().required(),
          otherwise: Yup.string(),
        }),
    },
    {
      label: () => t('ending', { defaultValue: 'Ending' }),
      fieldName: 'Ending',
      getInputState: () => 'required',
      showField: (ctx) => isScheduleEligible(ctx) && ctx.ScheduleType === 'recurring',
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});

        return <Select version="2" options={endingOptions} {...fieldData} />;
      },
      validate: () =>
        Yup.string().when(['PaymentMethod', 'ScheduleType', 'Currency'], {
          is: (PaymentMethod: string, ScheduleType: string, Currency: string) =>
            isScheduleEligible({ PaymentMethod, Currency }) && ScheduleType === 'recurring',
          then: Yup.string().required(),
          otherwise: Yup.string(),
        }),
    },
    {
      label: () => t('endDate', { defaultValue: 'End Date' }),
      fieldName: 'EndDate',
      getInputState: () => 'required',
      showField: (ctx) => isScheduleEligible(ctx) && ctx.ScheduleType === 'recurring' && ctx.Ending === 'End Date',
      renderField: ({ getFormikData, ctx }) => {
        const fieldData = getFormikData<string>({});

        return (
          <DatepickerSimple
            {...fieldData}
            variant="outlineShadow"
            placeholder="YYYY-MM-DD"
            iconPosition="left"
            min={addDays(new Date(Number(ctx.values.StartDate)), 1).getTime() as Timestamp}
            isDisabled={fieldData.disabled}
          />
        );
      },
      validate: () =>
        Yup.string().when(['PaymentMethod', 'ScheduleType', 'Ending', 'Currency'], {
          is: (PaymentMethod: string, ScheduleType: string, Ending: string, Currency: string) =>
            isScheduleEligible({ PaymentMethod, Currency }) && ScheduleType === 'recurring' && Ending === 'End Date',
          then: Yup.string()
            .test(
              'end-date-validation',
              t('ErrorMessage.invalidEndDate', {
                defaultValue: 'Invalid date. Please choose a date on or after start date',
              }),
              function (value) {
                if (!value || !this.parent?.StartDate) return true;
                const endDate = new Date(format(new Date(Number(value)), 'yyyy-MM-dd'));
                const startDate = new Date(format(new Date(Number(this.parent.StartDate)), 'yyyy-MM-dd'));

                return endDate > startDate;
              },
            )
            .required(),
          otherwise: Yup.string(),
        }),
    },
    {
      label: () => t('endingAfterPayments', { defaultValue: 'Ending After Payments' }),
      fieldName: 'EndingAfterPayments',
      getInputState: () => 'required',
      showField: (ctx) => ctx.Ending === 'Payments' && isScheduleEligible(ctx),
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});

        return <TextField variant="outlineShadow" preset="text" {...fieldData} />;
      },
      validate: () =>
        Yup.string().when(['ScheduleType', 'Ending'], {
          is: (ScheduleType: string, Ending: string) => ScheduleType === 'recurring' && Ending === 'Payments',
          then: Yup.string().required(),
          otherwise: Yup.string(),
        }),
    },
    {
      label: () => t('Description.label', { defaultValue: 'Description' }),
      fieldName: 'Description',
      getInputState: () => 'required',
      showField: (ctx) =>
        (ctx.ScheduleType === 'recurring' ||
          (!!ctx.TakeFromAccountOn &&
            ctx.TakeFromAccountOn !== '' &&
            !IsTodayDate(format(new Date(Number(ctx.TakeFromAccountOn)), 'yyyy-MM-dd')))) &&
        isScheduleEligible(ctx),
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});

        return (
          <TextField
            variant="outlineShadow"
            preset="text"
            placeholder={t('Description.placeholder', { defaultValue: 'Schedule Description' })}
            {...fieldData}
          />
        );
      },
      validate: () =>
        Yup.string().when(['PaymentMethod', 'ScheduleType', 'Currency', 'TakeFromAccountOn'], {
          is: (PaymentMethod: string, ScheduleType: string, Currency: string, TakeFromAccountOn: string) =>
            isScheduleEligible({ PaymentMethod, Currency }) &&
            (ScheduleType === 'recurring' ||
              (TakeFromAccountOn && !IsTodayDate(format(new Date(Number(TakeFromAccountOn)), 'yyyy-MM-dd')))),
          then: Yup.string().required(),
          otherwise: Yup.string(),
        }),
    },
    {
      label: () => t('messageForRecipient', { defaultValue: 'Message For Recipient' }),
      fieldName: 'MessageForRecipient',
      getInputState: () => 'optional',
      showField: (ctx) => ctx.PaymentMethod === 'Interac e-Transfer Money Request',
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});

        return (
          <TextField
            variant="outlineShadow"
            preset="text"
            maxLength={INTERAC_MESSAGE_MAX_LENGTH}
            showcaseLength
            {...fieldData}
          />
        );
      },
    },
  ];

  const additionalFields: FieldData<MyAccountFormData>[] = [
    {
      label: () => t('transactionLabel', { defaultValue: 'Transaction Label' }),
      fieldName: 'TransactionLabel',
      getInputState: () => 'optional',
      showField: (ctx) => TransactionLabelOverrideEnabled === '1' && ctx.PaymentMethod === 'Bank Account',
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});

        return <TextField variant="outlineShadow" preset="text" maxLength={30} {...fieldData} />;
      },
      validate: () =>
        Yup.string().max(
          30,
          t('ErrorMessage.transactionLabelMaximumLength', {
            defaultValue: 'The value supplied exceeds the maximum length (max. 30)',
          }),
        ),
    },
    {
      label: () => t('notes', { defaultValue: 'Notes' }),
      fieldName: 'Notes',
      getInputState: () => 'optional',
      showField: (ctx) => ctx.PaymentMethod !== 'Credit Card',
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});

        return <TextField variant="outlineShadow" preset="text" {...fieldData} />;
      },
    },
  ];

  return { accountFields, amountFields, scheduleFields, additionalFields };
};
